import {
    atom,
    atomFamily,
    CallbackInterface,
    RecoilState,
    RecoilValueReadOnly,
    selector,
} from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';

/**
 * The placed annotations of one kind, one atom each. The id list gives the
 * order; a row or a drag writes the one atom it edits, so nothing else
 * changes. An id is in the list exactly while its atom holds a record.
 */
export interface AnnotationListState<T extends AnnotationRecord> {
    /** Ids of the placed annotations, oldest first. */
    readonly ids: RecoilState<readonly string[]>;
    /** One annotation by id; undefined for an id that is not in the list. */
    readonly item: (id: string) => RecoilState<T | undefined>;
    /** Every placed annotation, oldest first, derived from the ids and their atoms. */
    readonly items: RecoilValueReadOnly<readonly T[]>;
}

/** The state of one kind of annotation; `key` prefixes its atom keys. */
export function annotationListState<T extends AnnotationRecord>(
    key: string
): AnnotationListState<T> {
    const ids = atom<readonly string[]>({
        key: `${key}IdsState`,
        default: [],
    });
    const item = atomFamily<T | undefined, string>({
        key: `${key}ItemState`,
        default: undefined,
    });
    const items = selector<readonly T[]>({
        key: `${key}ItemsState`,
        get: ({ get }) =>
            get(ids).flatMap((id) => {
                const placed = get(item(id));
                return placed === undefined ? [] : [placed];
            }),
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
    return { ids, item, items };
}

/** Appends an annotation: its atom is set and its id added, in one update. */
export function addAnnotation<T extends AnnotationRecord>(
    { set }: Pick<CallbackInterface, 'set'>,
    list: AnnotationListState<T>,
    annotation: T
): void {
    set(list.item(annotation.id), annotation);
    set(list.ids, (ids) => [...ids, annotation.id]);
}

/** Removes one annotation: its id from the list, its atom to the default. */
export function removeAnnotation<T extends AnnotationRecord>(
    { set, reset }: Pick<CallbackInterface, 'set' | 'reset'>,
    list: AnnotationListState<T>,
    id: string
): void {
    set(list.ids, (ids) => ids.filter((placed) => placed !== id));
    reset(list.item(id));
}

/**
 * Replaces one annotation with `edit` of it. An id whose atom is unset is
 * left unset, so an edit landing after a removal creates nothing. The id is
 * read from the snapshot, so this must not share a callback with an add of
 * the same annotation.
 */
export function updateAnnotation<T extends AnnotationRecord>(
    { snapshot, set }: Pick<CallbackInterface, 'snapshot' | 'set'>,
    list: AnnotationListState<T>,
    id: string,
    edit: (annotation: T) => T
): void {
    if (snapshot.getLoadable(list.item(id)).getValue() === undefined) {
        return;
    }
    set(list.item(id), (placed) =>
        placed === undefined ? placed : edit(placed)
    );
}

/**
 * Removes every annotation: the id list and each atom go back to default.
 * The ids are read from the snapshot, which predates writes queued earlier
 * in the same callback, so it must not share a callback with an add to the
 * same list.
 */
export function clearAnnotations<T extends AnnotationRecord>(
    { snapshot, reset }: Pick<CallbackInterface, 'snapshot' | 'reset'>,
    list: AnnotationListState<T>
): void {
    for (const id of snapshot.getLoadable(list.ids).getValue()) {
        reset(list.item(id));
    }
    reset(list.ids);
}
